import { Transcript } from '../../text/Transcript';
import type { BaseAssetSettings } from '../../asset/BaseAsset';
import { CanvasTextAssetStyle } from '../../canvasText/asset/CanvasTextAssetStyle';
import type { CanvasCaptionRendererSettings } from '../renderer/CanvasCaptionRenderer';
import { CanvasCaptionRenderer } from '../renderer/CanvasCaptionRenderer';
import {
    CanvasTextMetrics,
    tallestStringsFromTranscript
} from '../../canvasText/metrics/CanvasTextMetrics';
import type { CanvasTextStyleRendererSettings } from '../../canvasText/renderer/CanvasTextStyle';
import { CanvasTextStyleRenderer } from '../../canvasText/renderer/CanvasTextStyle';
import { BaseTextCanvasAsset } from '../../canvasRenderer/asset/BaseTextCanvasAsset';
import { CanvasFontSizeToFitAllTextBinarySearch } from '../../canvasText/metrics/CanvasFontSizeToFitAllTextBinarySearch';
import { buildMetricsForCaptions } from '../metrics/CanvasCaptionMetrics';

/**
 * CanvasCaptionAsset integrates our CanvasCaption renderer into Luminary and provides us with a way to render
 * transcript captions.
 */
export class CanvasCaptionAsset extends BaseTextCanvasAsset<CanvasCaptionRenderer> {
    override _classType = 'CanvasCaptionAsset';

    style: CanvasTextAssetStyle;

    transcript: Transcript = new Transcript({
        parent: this,
        redrawParentOnUpdate: true,
        fetchResources: this.fetchResources,
        autoUpdate: this.autoDraw,
    });

    constructor(container, settings: BaseAssetSettings) {
        const renderer = new CanvasCaptionRenderer();
        super(container, settings, renderer);

        const styleSettings = {
            parent: this,
            redrawParentOnUpdate: true,
            autoUpdate: true,
            referenceToOutputMultiplier: this.referenceToOutputMultiplier,
        };
        this.style = new CanvasTextAssetStyle(styleSettings);
    }

    override get _styles() {
        // We only support one style for the moment, while some assets have normal + hightlight style
        return { style: this.style };
    }

    get hasText() {
        return this.transcript.words.length !== 0;
    }

    /**
     * Create a bridge for all asset properties into canvas compatible properties. This includes transcript, style and
     * dynamic styling properties. The output is an object that can be passed to the canvas renderer build method.
     * */
    override getCanvasRendererSettings(): CanvasCaptionRendererSettings {
        this._syncFontSizeAndLeadingToStyles();
        return {
            ...super.getCanvasRendererSettings(),
            transcript: this.transcript,
            alignment: this.textAlign,
            style: this.style.getCanvasRendererSettings() as CanvasTextStyleRendererSettings,
        };
    }

    override set autoDraw(value: boolean) {
        super.autoDraw = value;
        // Because the transcript extends from TimeInvariantBase and isn't a BaseAsset,
        // we need to manually connect the two different update loops
        this.transcript.autoUpdate = value;
    }

    override get autoDraw() {
        return super.autoDraw;
    }

    override _setAutoDrawForAllChildren(value: boolean) {
        super._setAutoDrawForAllChildren(value);
        // connect the transcript to the autoDraw update loop
        this.transcript.autoUpdate = value;
    }

    /**
     * Returns each caption chunk as an SRT formatted string
     */
    async generateSRTText(timeOffset: number): Promise<string[]> {
        if (!this.hasText) {
            return [];
        }
        if (this.renderer.hasMetrics) {
            const chunks = this.renderer.getChunkTexts();
            const chunkTimeRanges = this.renderer.getChunkTimeRanges();
            return this._generateSRTText(timeOffset, chunks, chunkTimeRanges);
        }
        else {
            // the renderer metrics may not be built if it's not visible
            await this.onUsedFontsLoaded();
            const settings = this.getCanvasRendererSettings();
            const style = new CanvasTextStyleRenderer();
            style.apply(settings.style);

            const metrics = buildMetricsForCaptions({
                transcript: settings.transcript,
                alignment: settings.alignment,
                style,
                maxWidth: settings.maxTextWidth,
                maxHeight: settings.maxTextHeight,
            });
            const chunks = CanvasTextMetrics.getChunkTexts(metrics);
            const chunkTimeRanges = CanvasTextMetrics.getChunkTimeRanges(metrics, this.transcript);
            return this._generateSRTText(timeOffset, chunks, chunkTimeRanges);
        }
    }

    _generateSRTText(timeOffset: number, chunks: string[], chunkTimeRanges: number[][]): string[] {
        const result: string[] = [];
        for (let i = 0; i < chunks.length; i++) {
            const [startTime, endTime] = chunkTimeRanges[i];
            const startString = convertToSRTTimestamp(startTime + timeOffset);
            const endString = convertToSRTTimestamp(endTime + timeOffset);
            const srt = `${startString} --> ${endString}\n${chunks[i]}`;
            result.push(srt);
        }
        return result;
    }

    /** Sync the font size and leading to the style object. */
    _syncFontSizeAndLeadingToStyles() {
        const scaledFontSize = this.textSize * this.textScaleFactor;
        this.style.fontSize = scaledFontSize;
        this.style.leading = scaledFontSize * this.leadingMultiplier;
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'style',
            'transcript',
        ]);
    }

    override get readonlyProperties() {
        return [];
    }

    /**
     * applyTextAssetStyle allows us to load the style settings from a text asset onto our caption asset.
     */
    applyTextAssetStyle(settings) {
        // here we use a batchRedraw to avoid unnecessary rebuilds
        this._batchRedraw(() => {
            this.textSize = settings.textSize;
            this.textAlign = settings.textAlign;
            this.leadingMultiplier = settings.leadingMultiplier;
            this.textScaleFactor = settings.textScaleFactor;
            this.maxHeight = settings.maxHeight;
            this.maxWidth = settings.maxWidth;

            this.transformation.load(settings.transformation);

            const scaledFontSize = this.textSize * this.textScaleFactor;
            const fontSize = scaledFontSize;
            const leading = scaledFontSize * this.leadingMultiplier;
            this.style.loadTextAssetStyle({
                ...settings.normalStyle,
                fontSize,
                leading,
            });
        });
    }

    /** Returns the maximum font size for the given font that can fit on one line. */
    getFontSizeToFitTallestText(fontSizeRange = { min: 50, max: 250 }) {
        const settings = this.getCanvasRendererSettings();
        const style = new CanvasTextStyleRenderer();
        style.apply(settings.style);

        const canvasFontSizeRange = {
            min: Math.floor(fontSizeRange.min * this.referenceToOutputMultiplier),
            max: Math.floor(fontSizeRange.max * this.referenceToOutputMultiplier),
        };

        // get the tallest text that can fit on one line
        let tallestText = '';
        if (this.renderer.hasChunks) {
            tallestText = this.renderer.getTallestText();
        }
        else {
            tallestText = tallestStringsFromTranscript(this.transcript, style.fontFamily).join('');
        }

        // it's possible the transcript is empty (has no text)
        if (tallestText === '') {
            return {
                fontSize: this.style.fontSize,
                baseFontSize: Math.floor(this.style.fontSize / this.textScaleFactor),
                displaysAllText: true,
                tallestText: '',
            };
        }

        const { fontSize: canvasFontSize, displaysAllText } = CanvasFontSizeToFitAllTextBinarySearch.search({
            text: tallestText,
            alignment: settings.alignment,
            normalStyle: style,
            highlightStyle: style,
            highlightAreas: [],
            wordWrap: true,
            // since we're only after the tallest text that can fit on one line we don't need to worry about respecting
            // the current maxWidth which is why we're using a big number here. Also note that this is a canvas value,
            // not a luminary base value
            wordWrapWidth: 1920,
            maxHeight: settings.maxTextHeight,
            fontSizeRange: canvasFontSizeRange,
        });

        const assetFontSize = Math.floor(canvasFontSize / this.referenceToOutputMultiplier);

        return {
            fontSize: assetFontSize,
            baseFontSize: Math.floor(assetFontSize / this.textScaleFactor),
            displaysAllText,
            tallestText,
        };
    }
}

/**
 * Converts milliseconds into a string representation for SRT. The spec can be
 * found here: https://www.w3.org/TR/webvtt1/#webvtt-timestamp
 */
export function convertToSRTTimestamp(milliseconds: number) {
    let seconds = Math.floor(milliseconds / 1000);
    let minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    milliseconds = Math.floor(milliseconds % 1000);
    seconds = (seconds % 60);
    minutes = (minutes % 60);
    return hours.toString().padStart(2, '0') + ':' +
        minutes.toString().padStart(2, '0') + ':' +
        seconds.toString().padStart(2, '0') + ',' +
        milliseconds.toString().padStart(3, '0');
}
